"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { phCapture } from "@/utils/analytics";
import { dismissChecklist } from "@/lib/actions/onboarding-checklist";
import { safeGet, safeSet } from "@/components/onboarding/safe-storage";
import {
  getOnboardingTrack,
  OnboardingTrack,
} from "@/lib/billing/entitlements";

const STORAGE_PREFIX = "upsider_checklist";

function getKeys(userId?: string) {
  const scope = userId ? `${STORAGE_PREFIX}:${userId}` : STORAGE_PREFIX;
  return {
    collapsed: `${scope}:collapsed`,
    dismissed: `${scope}:dismissed`,
    visitedOptimizer: `${scope}:visited_optimizer`,
    clickedBetLink: `${scope}:clicked_bet_link`,
    visitedWhaleWatch: `${scope}:visited_whale_watch`,
    visitedEvSniper: `${scope}:visited_ev_sniper`,
    visitedCalculators: `${scope}:visited_calculators`,
    discordConnected: `${scope}:discord_connected`,
    shownTracked: `${scope}:shown_tracked`,
    completedTracked: `${scope}:completed_tracked`,
    completedAll: `${scope}:completed_all`,
  } as const;
}



export interface ChecklistItem {
  id: string;
  label: string;
  href?: string;
  description: string;
}

const ACCOUNT_EV_ITEM: ChecklistItem = {
  id: "account",
  label: "Create your account",
  description: "You're in — let's find your edge",
};

const ACCOUNT_WHALE_ITEM: ChecklistItem = {
  id: "account",
  label: "Create your account",
  description: "You're in — let's track the sharps",
};

const SPORTSBOOKS_ITEM: ChecklistItem = {
  id: "sportsbooks",
  label: "Set your sportsbooks",
  href: "/account#sportsbook-preferences",
  description: "Your defaults follow you across every betting tool",
};

const FIND_BET_ITEM: ChecklistItem = {
  id: "find_bet",
  label: "Find your first +EV bet",
  href: "/optimizer/props",
  description: "Green means the price beats the fair odds",
};

const FIRE_SNIPER_ITEM: ChecklistItem = {
  id: "fire_sniper",
  label: "Fire up the EV Sniper",
  href: "/ev-sniper",
  description: "Live +EV alerts the moment they hit the board",
};

const EXPLORE_WHALE_WATCH_ITEM: ChecklistItem = {
  id: "explore_whale_watch",
  label: "Explore Whale Watch",
  href: "/whale-watch",
  description: "See where the big money is moving",
};

const TRY_CALCULATOR_ITEM: ChecklistItem = {
  id: "try_calculator",
  label: "Try the EV Calculator",
  href: "/calculators/ev",
  description: "See if a line is worth betting — free forever",
};

const CLICK_BET_LINK_ITEM: ChecklistItem = {
  id: "click_bet_link",
  label: "Click a bet link",
  href: "/optimizer/props",
  description: "Opens your sportsbook — one tap to place it",
};

const CLICK_BET_LINK_WHALE_ITEM: ChecklistItem = {
  id: "click_bet_link",
  label: "Click a bet link",
  href: "/whale-watch",
  description: "Follow the smart money to your sportsbook",
};

const CONNECT_DISCORD_ITEM: ChecklistItem = {
  id: "connect_discord",
  label: "Join the VIP Discord",
  href: "/account#discord",
  description: "Exclusive plays and sharps to learn from",
};

export const ITEMS_BY_PLAN: Record<OnboardingTrack, ChecklistItem[]> = {
  "ev-plan": [ACCOUNT_EV_ITEM, SPORTSBOOKS_ITEM, FIND_BET_ITEM, CLICK_BET_LINK_ITEM],
  "whale-watch": [
    ACCOUNT_WHALE_ITEM,
    EXPLORE_WHALE_WATCH_ITEM,
    CLICK_BET_LINK_WHALE_ITEM,
    TRY_CALCULATOR_ITEM,
  ],
  "ev-sniper": [ACCOUNT_EV_ITEM, SPORTSBOOKS_ITEM, FIRE_SNIPER_ITEM, CLICK_BET_LINK_ITEM],
  gold: [
    ACCOUNT_EV_ITEM,
    SPORTSBOOKS_ITEM,
    FIND_BET_ITEM,
    EXPLORE_WHALE_WATCH_ITEM,
    CLICK_BET_LINK_ITEM,
  ],
  diamond: [
    ACCOUNT_EV_ITEM,
    SPORTSBOOKS_ITEM,
    FIRE_SNIPER_ITEM,
    EXPLORE_WHALE_WATCH_ITEM,
    CLICK_BET_LINK_ITEM,
    CONNECT_DISCORD_ITEM,
  ],
};

export const CELEBRATION_TEXT: Record<OnboardingTrack, string> = {
  "ev-plan": "You're ready to compare the board.",
  "whale-watch": "You're ready to follow the smart money!",
  "ev-sniper": "The Sniper's locked on — go grab your first alert!",
  gold: "You're set to find edges and follow the sharps!",
  diamond: "You've got the full stack — Sniper, Whale Watch, and the board are all yours.",
};

const ITEM_IDS_BY_PLAN = Object.fromEntries(
  Object.entries(ITEMS_BY_PLAN).map(([plan, planItems]) => [
    plan,
    planItems.map((item) => item.id),
  ]),
) as Record<OnboardingTrack, string[]>;

interface UseChecklistStateProps {
  hasOnboardingPreferences: boolean;
  checklistDismissed: boolean;
}

export function useChecklistState({
  hasOnboardingPreferences,
  checklistDismissed,
}: UseChecklistStateProps) {
  const { user, subscriptions } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const userId = user?.id;
  const keys = useMemo(() => getKeys(userId), [userId]);

  const detectedPlanType = getOnboardingTrack(subscriptions);

  const planOverride =
    process.env.NODE_ENV !== "production"
      ? (searchParams?.get("checklist_plan") as OnboardingTrack | null)
      : null;
  const planType =
    planOverride && planOverride in ITEMS_BY_PLAN
      ? planOverride
      : detectedPlanType;

  const resolvedPlanType: OnboardingTrack = planType ?? "ev-plan";

  const isTrialUser =
    (planOverride != null && planOverride in ITEMS_BY_PLAN) ||
    subscriptions.some((s) => s.status === "trialing");

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [visitedOptimizer, setVisitedOptimizer] = useState(false);
  const [clickedBetLink, setClickedBetLink] = useState(false);
  const [visitedWhaleWatch, setVisitedWhaleWatch] = useState(false);
  const [visitedEvSniper, setVisitedEvSniper] = useState(false);
  const [visitedCalculators, setVisitedCalculators] = useState(false);
  const [discordConnected, setDiscordConnected] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const shownTrackedRef = useRef(false);
  const completionTrackedRef = useRef(false);
  const completedTrackedRef = useRef<Set<string>>(new Set());
  const autoDismissedRef = useRef(false);
  const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const items = ITEMS_BY_PLAN[resolvedPlanType];
  const itemIds = ITEM_IDS_BY_PLAN[resolvedPlanType];

  useEffect(() => {
    if (!userId) {
      setMounted(false);
      return;
    }

    setIsCollapsed(safeGet(keys.collapsed) === "1");
    setIsDismissed(checklistDismissed || safeGet(keys.dismissed) === "1");
    setVisitedOptimizer(safeGet(keys.visitedOptimizer) === "1");
    setClickedBetLink(safeGet(keys.clickedBetLink) === "1");
    setVisitedWhaleWatch(safeGet(keys.visitedWhaleWatch) === "1");
    setVisitedEvSniper(safeGet(keys.visitedEvSniper) === "1");
    setVisitedCalculators(safeGet(keys.visitedCalculators) === "1");
    setDiscordConnected(safeGet(keys.discordConnected) === "1");

    shownTrackedRef.current = safeGet(keys.shownTracked) === "1";
    completionTrackedRef.current = safeGet(keys.completedAll) === "1";
    completedTrackedRef.current = new Set(
      (safeGet(keys.completedTracked)?.split(",").filter(Boolean) ?? []),
    );
    autoDismissedRef.current = false;
    setIsAnimatingOut(false);

    setMounted(true);
  }, [userId, checklistDismissed, keys]);

  useEffect(() => {
    if (!mounted || !userId || !isTrialUser) return;

    if (pathname?.startsWith("/optimizer") && !visitedOptimizer) {
      safeSet(keys.visitedOptimizer, "1");
      setVisitedOptimizer(true);
    }
    if (
      (pathname === "/whale-watch" || pathname?.startsWith("/whale-watch/")) &&
      !visitedWhaleWatch
    ) {
      safeSet(keys.visitedWhaleWatch, "1");
      setVisitedWhaleWatch(true);
    }
    if (
      (pathname === "/ev-sniper" ||
        pathname?.startsWith("/ev-sniper/") ||
        pathname === "/edge-finder" ||
        pathname?.startsWith("/edge-finder/")) &&
      !visitedEvSniper
    ) {
      safeSet(keys.visitedEvSniper, "1");
      setVisitedEvSniper(true);
    }
    if (pathname?.startsWith("/calculators") && !visitedCalculators) {
      safeSet(keys.visitedCalculators, "1");
      setVisitedCalculators(true);
    }
  }, [
    pathname,
    mounted,
    isTrialUser,
    visitedOptimizer,
    visitedWhaleWatch,
    visitedEvSniper,
    visitedCalculators,
    userId,
    keys,
  ]);

  useEffect(() => {
    if (!mounted || !userId || !isTrialUser || discordConnected) return;
    if (!itemIds.includes("connect_discord")) return;
    let cancelled = false;
    fetch("/api/discord/status")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { connected?: boolean } | null) => {
        if (cancelled || !data?.connected) return;
        safeSet(keys.discordConnected, "1");
        setDiscordConnected(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mounted, userId, isTrialUser, discordConnected, itemIds, keys, pathname]);

  useEffect(() => {
    if (!mounted || !userId || !isTrialUser || clickedBetLink) return;

    function handleBetLinkClick() {
      safeSet(keys.clickedBetLink, "1");
      setClickedBetLink(true);
    }

    window.addEventListener("upsider:bet_link_clicked", handleBetLinkClick);
    return () =>
      window.removeEventListener(
        "upsider:bet_link_clicked",
        handleBetLinkClick,
      );
  }, [mounted, userId, isTrialUser, clickedBetLink, keys]);

  const completionLookup: Record<string, boolean> = {
    account: true,
    sportsbooks: hasOnboardingPreferences,
    find_bet: visitedOptimizer,
    click_bet_link: clickedBetLink,
    explore_whale_watch: visitedWhaleWatch,
    fire_sniper: visitedEvSniper,
    try_calculator: visitedCalculators,
    connect_discord: discordConnected,
  };

  const completedCount = itemIds.filter((id) => completionLookup[id]).length;
  const totalCount = items.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);
  const allComplete = completedCount === totalCount;

  const isItemComplete = useCallback(
    (itemId: string) => !!completionLookup[itemId],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasOnboardingPreferences,
      visitedOptimizer,
      clickedBetLink,
      visitedWhaleWatch,
      visitedEvSniper,
      visitedCalculators,
      discordConnected,
      itemIds,
      resolvedPlanType,
    ],
  );

  useEffect(() => {
    if (!mounted || !userId || !isTrialUser || isDismissed) return;
    if (shownTrackedRef.current || safeGet(keys.shownTracked) === "1") return;
    shownTrackedRef.current = true;
    safeSet(keys.shownTracked, "1");
    phCapture("checklist_shown", { plan_type: resolvedPlanType });
  }, [mounted, userId, isTrialUser, isDismissed, resolvedPlanType, keys]);

  useEffect(() => {
    if (!mounted || !userId || !isTrialUser) return;
    const entries: [string, boolean][] = [
      ["sportsbooks", hasOnboardingPreferences],
      ["find_bet", visitedOptimizer],
      ["click_bet_link", clickedBetLink],
      ["explore_whale_watch", visitedWhaleWatch],
      ["fire_sniper", visitedEvSniper],
      ["try_calculator", visitedCalculators],
    ];
    const tracked = new Set([
      ...completedTrackedRef.current,
      ...(safeGet(keys.completedTracked)?.split(",").filter(Boolean) ?? []),
    ]);
    let fired = false;
    for (const [id, done] of entries) {
      if (!done || tracked.has(id) || !itemIds.includes(id)) continue;
      tracked.add(id);
      fired = true;
      phCapture("checklist_item_completed", {
        item: id,
        items_completed: completedCount,
        total_items: totalCount,
        plan_type: resolvedPlanType,
      });
    }
    completedTrackedRef.current = tracked;
    if (fired) safeSet(keys.completedTracked, [...tracked].join(","));
  }, [
    mounted,
    userId,
    isTrialUser,
    resolvedPlanType,
    hasOnboardingPreferences,
    visitedOptimizer,
    clickedBetLink,
    visitedWhaleWatch,
    visitedEvSniper,
    visitedCalculators,
    completedCount,
    totalCount,
    itemIds,
    keys,
  ]);

  useEffect(() => {
    if (!mounted || !userId || !isTrialUser || !allComplete) return;
    if (completionTrackedRef.current || safeGet(keys.completedAll) === "1") return;
    completionTrackedRef.current = true;
    safeSet(keys.completedAll, "1");
    phCapture("checklist_completed", { plan_type: resolvedPlanType });
  }, [mounted, userId, isTrialUser, allComplete, resolvedPlanType, keys]);

  useEffect(() => {
    if (!mounted || !allComplete || isDismissed || isAnimatingOut) return;

    autoDismissTimerRef.current = setTimeout(() => {
      setIsAnimatingOut(true);
    }, 3000);

    return () => {
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
        autoDismissTimerRef.current = null;
      }
    };
  }, [mounted, allComplete, isDismissed, isAnimatingOut, userId]);

  useEffect(() => {
    if (!mounted || !userId || !isAnimatingOut || autoDismissedRef.current) return;
    const fallback = setTimeout(() => {
      if (autoDismissedRef.current) return;
      autoDismissedRef.current = true;
      setIsDismissed(true);
      safeSet(keys.dismissed, "1");
      phCapture("checklist_dismissed", {
        items_completed: completedCount,
        auto: true,
        plan_type: resolvedPlanType,
      });
      dismissChecklist().catch(() => {});
    }, 600);
    return () => clearTimeout(fallback);
  }, [mounted, userId, isAnimatingOut, completedCount, resolvedPlanType, keys]);

  const handleToggle = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      safeSet(keys.collapsed, next ? "1" : "0");
      return next;
    });
  }, [keys]);

  const handleDismiss = useCallback(() => {
    if (autoDismissedRef.current) return;
    autoDismissedRef.current = true;
    setIsDismissed(true);
    safeSet(keys.dismissed, "1");
    phCapture("checklist_dismissed", {
      items_completed: completedCount,
      plan_type: resolvedPlanType,
    });
    dismissChecklist().catch(() => {});
  }, [completedCount, resolvedPlanType, keys]);

  const handleItemClick = useCallback(
    (item: ChecklistItem) => {
      phCapture("checklist_item_clicked", {
        item: item.id,
        plan_type: resolvedPlanType,
      });
    },
    [resolvedPlanType],
  );

  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (
        !isAnimatingOut ||
        autoDismissedRef.current ||
        e.propertyName !== "opacity" ||
        e.target !== e.currentTarget
      )
        return;
      autoDismissedRef.current = true;
      setIsDismissed(true);
      safeSet(keys.dismissed, "1");
      phCapture("checklist_dismissed", {
        items_completed: completedCount,
        auto: true,
        plan_type: resolvedPlanType,
      });
      dismissChecklist().catch(() => {});
    },
    [isAnimatingOut, completedCount, resolvedPlanType, keys],
  );

  const shouldShow = mounted && !!userId && isTrialUser && !isDismissed;

  return {
    planType: resolvedPlanType,

    items,
    completedCount,
    totalCount,
    progressPercent,
    allComplete,
    isItemComplete,

    mounted,
    isTrialUser,
    isDismissed,
    shouldShow,
    isCollapsed,
    isAnimatingOut,

    isCelebrating: allComplete && !isDismissed,

    handleToggle,
    handleDismiss,
    handleItemClick,
    handleTransitionEnd,
  };
}
